import { ButtonContainer, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import { FcbForfaitAuditeurDto, ForfaitAuditeurApi, ForfaitAuditeurSearch } from "../../../../api";
import { ERoutes } from "../../../../AppRouter";
import { EditButton, SearchTablePage, ViewButton } from "../../../../components";
import { useTl } from "../../../../hooks";
import { useAbortableApiServiceFactory, useApiService } from "../../../../hooks/useApiService";
import { ETLCodes } from "../../../../locales";

export interface IForfaitAuditeurListProps {}

export const ForfaitAuditeurList: React.FunctionComponent<IForfaitAuditeurListProps> = () => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(ForfaitAuditeurApi);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: FcbForfaitAuditeurDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.forfaitAuditeur}/${row.idForfait}/detail/view`)}
            />
            <EditButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.forfaitAuditeur}/${row.idForfait}/detail/edit`)}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.AnneeScolaire),
        fieldName: "anneeAcademique"
      },
      {
        header: () => t(ETLCodes.AaS1),
        fieldName: "aaS1"
      },
      {
        header: () => t(ETLCodes.AaS2),
        fieldName: "aaS2"
      },
      {
        header: () => t(ETLCodes.ABS1),
        fieldName: "abS1"
      },
      {
        header: () => t(ETLCodes.ABS2),
        fieldName: "abS2"
      },
      {
        header: () => t(ETLCodes.AIS1),
        fieldName: "aiS1"
      },
      {
        header: () => t(ETLCodes.AIS2),
        fieldName: "aiS2"
      },
      {
        header: () => t(ETLCodes.CEAS1),
        fieldName: "ceaS1"
      },
      {
        header: () => t(ETLCodes.CEAS2),
        fieldName: "ceaS2"
      },
      {
        header: () => t(ETLCodes.CEBS1),
        fieldName: "cebS1"
      },
      {
        header: () => t(ETLCodes.CEBS2),
        fieldName: "cebS2"
      },
      {
        header: () => t(ETLCodes.CEIS1),
        fieldName: "ceiS1"
      },
      {
        header: () => t(ETLCodes.CEIS2),
        fieldName: "ceiS2"
      },
      {
        header: () => t(ETLCodes.FAGS1),
        fieldName: "fagS1"
      },
      {
        header: () => t(ETLCodes.FAGS2),
        fieldName: "fagS2"
      }
    ],
    [history, t]
  );

  const onAddItem = React.useCallback(() => {
    history.push(`${ERoutes.forfaitAuditeur}/0/detail/edit`);
  }, [history]);

  const getCriteriasFn = React.useCallback(() => api.forfaitAuditeurGetSearchCriterias({ includeListsValues: true }), [
    api
  ]);

  const apiFactory = useAbortableApiServiceFactory(ForfaitAuditeurApi);
  const lastAbortController = React.useRef<AbortController>();
  const searchFn = React.useCallback(
    (nextSearch?: ForfaitAuditeurSearch) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.forfaitAuditeurBaseSearch({ ForfaitAuditeurSearch: nextSearch });
    },
    [apiFactory]
  );

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={getCriteriasFn}
      searchFunction={searchFn}
      onAbort={onAbort}
      addFunc={onAddItem}
      breadCrumbs={[{ text: t(ETLCodes.ForfaitAuditeur), route: ERoutes.forfaitAuditeur }]}
      sortKeys={{ anneeAcademique: "ASC" }}
    />
  );
};
